import httpService from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/ElementosDocumentosPdf_SalvarInput';
import '../contracts/ElementosDocumentosPdf_DeleteResponse';
import '../contracts/ElementosDocumentosPdf_GetResponse';
import '../contracts/ElementosDocumentosPdf_ListResponse';
import '../contracts/ElementosDocumentosPdf_SalvarResponse';
import type { ElementosDocumentosPdf_SalvarInput } from '../contracts/ElementosDocumentosPdf_SalvarInput';
/**
 * Composable for managing PDF Elements (headers/footers)
 */
export function usePDFElements() {

  /**
   * Fetch all PDF elements
   * @returns {Promise<Array>} List of PDF elements
   */
  const fetchPDFElements = async () => {
    log('usePDFElements', 'fetchPDFElements', 'Fetching all PDF elements');

    try {
      const response = await httpService.get('/index.php/config/elementos_documentos_pdf/buscar_elementos', { responseContract: 'ElementosDocumentosPdf_ListResponse' });

      if (response.success) {
        log('usePDFElements', 'fetchPDFElements', 'PDF elements fetched successfully', response.dados);
        return response.dados;
      } else {
        throw new Error(response.error || 'Failed to fetch PDF elements');
      }
    } catch (error) {
      log('usePDFElements', 'fetchPDFElements', 'Error fetching PDF elements', error);
      throw error;
    }
  };

  /**
   * Fetch a single PDF element by name
   * @param {String} name - Element name
   * @returns {Promise<Object>} PDF element data
   */
  const fetchPDFElementByName = async (name: any) => {
    log('usePDFElements', 'fetchPDFElementByName', 'Fetching PDF element', name);

    try {
      const response = await httpService.get(`/index.php/config/elementos_documentos_pdf/getByName/${name}`, { responseContract: 'ElementosDocumentosPdf_GetResponse' });

      if (response.success) {
        log('usePDFElements', 'fetchPDFElementByName', 'PDF element fetched successfully', response.dados);
        return response.dados;
      } else {
        throw new Error(response.error || 'Failed to fetch PDF element');
      }
    } catch (error) {
      log('usePDFElements', 'fetchPDFElementByName', 'Error fetching PDF element', error);
      throw error;
    }
  };

  /**
   * Save PDF element (create or update)
   * @param {Object} elementData - Element data
   * @returns {Promise<Object>} Response data
   */
  const savePDFElement = async (elementData: ElementosDocumentosPdf_SalvarInput) => {
    log('usePDFElements', 'savePDFElement', 'Saving PDF element', elementData);

    try {
      validate('ElementosDocumentosPdf_SalvarInput', elementData);
      const response = await httpService.post('/index.php/config/elementos_documentos_pdf/salvar', elementData, { responseContract: 'ElementosDocumentosPdf_SalvarResponse' });

      if (response.success) {
        log('usePDFElements', 'savePDFElement', 'PDF element saved successfully', response.dados);
        return response.dados;
      } else {
        throw new Error(response.error || 'Failed to save PDF element');
      }
    } catch (error) {
      log('usePDFElements', 'savePDFElement', 'Error saving PDF element', error);
      throw error;
    }
  };

  /**
   * Delete a PDF element
   * @param {String} name - Element name
   * @returns {Promise<void>}
   */
  const deletePDFElement = async (name: string) => {
    log('usePDFElements', 'deletePDFElement', 'Deleting PDF element', name);

    try {
      const response = await httpService.post(`/index.php/config/elementos_documentos_pdf/delete/${name}`, null, { responseContract: 'ElementosDocumentosPdf_DeleteResponse', inputContract: 'ElementosDocumentosPdf_DeleteInput' });

      if (response.success) {
        log('usePDFElements', 'deletePDFElement', 'PDF element deleted successfully');
        return;
      } else {
        throw new Error(response.error || 'Failed to delete PDF element');
      }
    } catch (error) {
      log('usePDFElements', 'deletePDFElement', 'Error deleting PDF element', error);
      throw error;
    }
  };

  /**
   * Convert a File object to base64 string with compression
   * @param {File} file - File to convert
   * @param {Number} maxWidth - Maximum width (default: 800px)
   * @param {Number} quality - Image quality 0-1 (default: 0.7)
   * @returns {Promise<String>} Compressed base64 string
   */
  const convertFileToBase64 = (file: any, maxWidth = 800, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        const img = new Image();

        img.onload = () => {
          // Create canvas for compression
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Calculate new dimensions maintaining aspect ratio
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          // Set canvas dimensions
          canvas.width = width;
          canvas.height = height;

          // Draw image on canvas with compression
          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
          }

          // Convert to base64 with quality compression
          // For JPEG: quality parameter works (0.7 = 70% quality)
          // For PNG: quality parameter is ignored, but resizing still reduces size
          const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
          const compressedBase64 = canvas.toDataURL(mimeType, quality);

          log('usePDFElements', 'convertFileToBase64', 'Image compressed', {
            originalSize: e.target.result.length,
            compressedSize: compressedBase64.length,
            reduction: Math.round((1 - compressedBase64.length / e.target.result.length) * 100) + '%'
          });

          resolve(compressedBase64);
        };

        img.onerror = (error: any) => {
          reject(new Error('Failed to load image for compression'));
        };

        img.src = e.target.result;
      };

      reader.onerror = (error: any) => {
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  };

  return {
    fetchPDFElements,
    fetchPDFElementByName,
    savePDFElement,
    deletePDFElement,
    convertFileToBase64
  };
}
